import { useCallback, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import classNames from "classnames";

import CloseIcon from "assets/icons/close.svg?react";
import NotaWordmark from "assets/icons/nota.svg?react";
import NotaMark from "assets/icons/notaicon.svg?react";
import PersonIcon from "assets/icons/person.svg?react";
import TeamIcon from "assets/icons/team.svg?react";
import LeftArrow from "assets/icons/left-arrow.svg?react";

import Input from "components/Input";
import ContactFormWizard from "components/ContactFormWizard";
import { useCreateTeam } from "hooks/useCreateTeam";
import settingsService from "api/settingsService";
import { setWorkspaceId } from "utils/setWorkspaceId";
import { ENotificationType, notification } from "utils/notification";
import { getErrorMessage } from "utils/getErrorMessage";
import { IError } from "dto/IError";

import styles from "./styles.module.scss";

interface Props {
  /** Zamyka kreator (ustawia modals.initialConfig = false). */
  onClose: () => void;
}

type Phase = "choose" | "team" | "contactForm";
type Usage = "private" | "company";

// Nagłówek kreatora pierwszej aktywacji — używany też przez ContactFormWizard
// (przekazywany jako prop `header`), aby krok konfiguracji formularza wyglądał
// jak kolejny krok tego samego kreatora.
const ActivationHeaderContent = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.headerContent}>
      <div className={styles.brandMark}>
        <NotaMark />
      </div>
      <div>
        <div className={styles.title}>{t("firstActivation.title")}</div>
        <div className={styles.subtitle}>{t("firstActivation.subtitle")}</div>
      </div>
    </div>
  );
};

/**
 * Kreator pierwszej aktywacji pokazywany po pierwszym wejściu do aplikacji
 * (wyzwalany przez ShowFirstConfiguration w ProtectedRoute). Krok 1: wybór
 * sposobu używania (prywatnie/firmowo). Prywatnie → od razu ContactFormWizard.
 * Firmowo → krok 2 (nazwa zespołu) → utworzenie zespołu + przełączenie na profil
 * zespołowy → ContactFormWizard. Zakończenie aktywacji oznaczane jest w momencie
 * otwarcia ContactFormWizard (lub zamknięcia kreatora).
 */
const FirstActivationWizard = ({ onClose }: Props) => {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>("choose");
  const [usage, setUsage] = useState<Usage>("private");
  const [teamName, setTeamName] = useState("");
  const [contactWorkspaceId, setContactWorkspaceId] = useState<
    string | undefined
  >(undefined);
  const [busy, setBusy] = useState(false);

  const { createTeam } = useCreateTeam();
  const { mutate: setFirstConfigCompleted } =
    settingsService.useSetInitialConfigurationCompleted();

  // Aktywację oznaczamy jako wykonaną tylko raz, niezależnie od ścieżki.
  const completedRef = useRef(false);
  // Synchroniczny strażnik przed podwójnym wysłaniem (szybki podwójny klik na
  // wolnej sieci, zanim `busy` zdąży się ustawić i zablokować przycisk).
  const inFlightRef = useRef(false);
  const markCompleteOnce = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    setFirstConfigCompleted({});
  }, [setFirstConfigCompleted]);

  const closeAll = useCallback(() => {
    markCompleteOnce();
    onClose();
  }, [markCompleteOnce, onClose]);

  const openContactForm = useCallback(
    (workspaceId?: string) => {
      setContactWorkspaceId(workspaceId);
      markCompleteOnce();
      setPhase("contactForm");
    },
    [markCompleteOnce],
  );

  const handleConfirmChoice = () => {
    if (usage === "private") {
      // Ścieżka prywatna: użytkownik nie zakłada firmy, więc nie ma WorkspaceId.
      openContactForm(undefined);
    } else {
      setPhase("team");
    }
  };

  const handleCreateTeam = async () => {
    const name = teamName.trim();
    if (busy || inFlightRef.current || !name) return;

    inFlightRef.current = true;
    setBusy(true);
    try {
      const workspaceId = await createTeam(name);
      setWorkspaceId(workspaceId);

      openContactForm(workspaceId);
    } catch (e: unknown) {
      notification({
        type: ENotificationType.ERROR,
        title: t("errors.error"),
        text: getErrorMessage(e as IError) || t("errors.somethingWentWrong"),
      });
    } finally {
      setBusy(false);
      inFlightRef.current = false;
    }
  };

  if (phase === "contactForm") {
    return (
      <ContactFormWizard
        open
        header={<ActivationHeaderContent />}
        initialSettings={
          contactWorkspaceId ? { WorkspaceId: contactWorkspaceId } : undefined
        }
        onClose={closeAll}
        onFinish={closeAll}
      />
    );
  }

  return ReactDOM.createPortal(
    <div className={styles.modal} role="dialog" aria-modal="true">
      <div className={styles.canvasBackground}>
        <div className={styles.bgl} />
        <div className={styles.bgr} />
      </div>

      <div className={styles.header}>
        <ActivationHeaderContent />
        <button
          type="button"
          className={styles.closeBtn}
          onClick={closeAll}
          aria-label={t("common.close")}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.content}>
        <div className={styles.cardOuter}>
          <div className={styles.card}>
            <div className={styles.cardContent}>
              {phase === "choose" ? (
                <>
                  <div className={styles.titleBlock}>
                    <div className={styles.cardTitle}>
                      {t("firstActivation.choose.title")}
                    </div>
                    <div className={styles.cardDesc}>
                      {t("firstActivation.choose.description")}
                    </div>
                  </div>

                  <div className={styles.choices}>
                    <button
                      type="button"
                      className={classNames(
                        styles.choiceCard,
                        usage === "private" && styles.selected,
                      )}
                      onClick={() => setUsage("private")}
                    >
                      <div className={styles.choiceIcon}>
                        <PersonIcon />
                      </div>
                      <div className={styles.choiceTitle}>
                        {t("firstActivation.choose.private.title")}
                      </div>
                      <div className={styles.choiceDesc}>
                        {t("firstActivation.choose.private.description")}
                      </div>
                    </button>

                    <button
                      type="button"
                      className={classNames(
                        styles.choiceCard,
                        usage === "company" && styles.selected,
                      )}
                      onClick={() => setUsage("company")}
                    >
                      <div className={styles.choiceIcon}>
                        <TeamIcon />
                      </div>
                      <div className={styles.choiceTitle}>
                        {t("firstActivation.choose.company.title")}
                      </div>
                      <div className={styles.choiceDesc}>
                        {t("firstActivation.choose.company.description")}
                      </div>
                    </button>
                  </div>

                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={handleConfirmChoice}
                  >
                    {t("firstActivation.confirm")}
                  </button>

                  <div className={styles.steps}>
                    <div
                      className={classNames(styles.stepBar, styles.active)}
                    />
                    <div className={styles.stepBar} />
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.titleBlock}>
                    <div className={styles.cardTitle}>
                      {t("firstActivation.team.title")}
                    </div>
                    <div className={styles.cardDesc}>
                      {t("firstActivation.team.description")}
                    </div>
                  </div>

                  <div className={styles.nameField}>
                    <Input
                      value={teamName}
                      label={t("firstActivation.team.nameLabel")}
                      onChange={(v) => setTeamName(v)}
                      onAccept={() => handleCreateTeam()}
                      focus
                    />
                  </div>

                  <div className={styles.actionsRow}>
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={() => setPhase("choose")}
                      disabled={busy}
                    >
                      <LeftArrow />
                      {t("common.back")}
                    </button>
                    <button
                      type="button"
                      className={styles.primaryBtn}
                      onClick={handleCreateTeam}
                      disabled={busy || !teamName.trim()}
                    >
                      {busy ? (
                        <span className={styles.spinner} aria-hidden="true" />
                      ) : (
                        t("firstActivation.confirm")
                      )}
                    </button>
                  </div>

                  <div className={styles.steps}>
                    <div className={styles.stepBar} />
                    <div
                      className={classNames(styles.stepBar, styles.active)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.poweredBy}>
        <span>{t("firstActivation.poweredBy")}</span>
        <NotaWordmark />
      </div>
    </div>,
    document.body,
  );
};

export default FirstActivationWizard;
